import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import { FILE_ICONS } from '../data/decisionsData';
import { getAccessToken, isSecretaryContextUser, authHeaders } from '../authStorage';
import { branchAttachmentDownloadUrl, fetchBranchById } from '../branchesApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './DecisionsPage.css';

function BranchDetailPage() {
  const { direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const branchId = Number(idParam);
  const invalidId = !Number.isFinite(branchId) || branchId < 1;

  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(!invalidId);
  const [loadError, setLoadError] = useState('');
  const [branch, setBranch] = useState(null);

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setAccessDenied(true); setLoading(false); return; }
    if (invalidId) { setLoadError(t('branches.invalidId')); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoadError(''); setLoading(true);
      try {
        const row = await fetchBranchById(branchId);
        if (!cancelled) setBranch(row);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('branches.loadDetailFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, branchId, invalidId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openContractDocument = async (contract) => {
    const openedTab = window.open('about:blank', '_blank');
    if (!openedTab) { setLoadError(t('branches.openFileFailed')); return; }
    const writeTabHtml = (title, body) => {
      openedTab.document.open();
      openedTab.document.write(`<!DOCTYPE html><html dir="${direction}"><head><meta charset="utf-8"><title>${title}</title>
      <style>body{font-family:system-ui,sans-serif;padding:24px;background:#f5fdfb;color:#00334a;}
      h1{font-size:1.1rem;margin:0 0 8px;}p{color:#607d8b;line-height:1.6;}</style></head><body>${body}</body></html>`);
      openedTab.document.close();
    };
    writeTabHtml(contract.name || 'Attachment', `<h1>${t('common.loading')}</h1>`);
    try {
      const url = branchAttachmentDownloadUrl(branchId, contract.id);
      const res = await fetch(url, { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error(t('branches.openFileFailed'));
      const blob = await res.blob();
      if (!blob || blob.size === 0) throw new Error(t('branches.openFileFailed'));
      const objectUrl = URL.createObjectURL(blob);
      openedTab.location.replace(objectUrl);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (e) {
      writeTabHtml(contract.name || 'Attachment', `<h1>${t('branches.openFileFailed')}</h1><p>${e instanceof Error ? e.message : ''}</p>`);
      setLoadError(e instanceof Error ? e.message : t('branches.openFileFailed'));
    }
  };

  if (accessDenied) {
    return (
      <div className="page" dir={direction}>
        <div className="page__topbar">
          <div className="page__topbar-left">
            <span className="page__topbar-title">{t('branches.pageTitle')}</span>
            <span className="page__topbar-sub">{t('branches.ceaOnly')}</span>
          </div>
        </div>
        <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>
      </div>
    );
  }

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <button type="button" className="btn btn--ghost" onClick={() => navigate('/app/branches')}>
                <Icon name="chevron-right" size={14} /> {t('common.back')}
              </button>
              <span className="list-page-card__title">
                <Icon name="branch" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />
                {t('branches.pageTitle')}
              </span>
            </div>
            <span className="list-page-card__sub">{branch?.name || (loading ? t('branches.loading') : '')}</span>
          </div>
          {branch && (
            <div className="list-page-card__actions">
              <button type="button" className="btn btn--primary" onClick={() => navigate(`/app/branches/${branch.id}/edit`)}>
                <Icon name="edit" size={14} color="#fff" /> {t('branches.editBranch')}
              </button>
            </div>
          )}
        </header>

        {loadError && <div className="list-page-card__banner list-page-card__banner--error">{loadError}</div>}
        {loading && <div className="list-page-card__loading">{t('branches.loading')}</div>}

      {!loading && branch && (
        <div className="table-card" style={{ padding: '20px 22px' }}>
          <div className="mform">
            <div className="mform__row mform__row--4">
              <div className="mform__field"><label className="mform__label">{t('branches.branchName')}</label><div className="mform__input-wrap"><Icon name="branch" size={14} color="#02c3a6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={branch.name || ''} disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('branches.crn')}</label><div className="mform__input-wrap"><Icon name="report" size={14} color="#1b8eff" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={branch.crn || ''} dir="ltr" disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('common.phone')}</label><div className="mform__input-wrap"><Icon name="user" size={14} color="#8b5cf6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={branch.phone || ''} dir="ltr" disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('common.email')}</label><div className="mform__input-wrap"><Icon name="chat" size={14} color="#ff8c42" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={branch.email || ''} dir="ltr" disabled /></div></div>
            </div>
            <div className="mform__row mform__row--4">
              <div className="mform__field"><label className="mform__label">{t('branches.registeredCapital')}</label><div className="mform__input-wrap"><Icon name="money" size={14} color="#14b8a6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={branch.registeredCapital || ''} dir="ltr" disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('branches.paidInCapital')}</label><div className="mform__input-wrap"><Icon name="money" size={14} color="#22c55e" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={branch.paidInCapital || ''} dir="ltr" disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('branches.authorizedCapital')}</label><div className="mform__input-wrap"><Icon name="money" size={14} color="#0ea5e9" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={branch.authorizedCapital || ''} dir="ltr" disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('branches.sharesCount')}</label><div className="mform__input-wrap"><Icon name="report" size={14} color="#a855f7" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={branch.sharesCount || ''} dir="ltr" disabled /></div></div>
            </div>
            <div className="mform__row mform__row--4">
              <div className="mform__field mform__field--span2"><label className="mform__label">{t('branches.address')}</label><div className="mform__input-wrap"><Icon name="building" size={14} color="#0ea5a4" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={branch.address || ''} disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('branches.shareParValue')}</label><div className="mform__input-wrap"><Icon name="money" size={14} color="#f59e0b" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={branch.shareParValue || ''} dir="ltr" disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('common.status')}</label><div className="mform__input-wrap"><Icon name="settings" size={14} color="#14b8a6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={branch.status === 'active' ? t('common.active') : t('common.inactive')} disabled /></div></div>
            </div>
            <div className="mform__row">
              <div className="mform__field mform__field--full"><label className="mform__label">{t('common.notes')}</label><textarea className="mform__textarea" value={branch.notes || ''} rows={3} disabled /></div>
            </div>

            <div className="branch-view__contracts-head">
              <Icon name="report" size={16} color="#02c3a6" />
              <span>{t('branches.contractsHead')}</span>
              <span className="branch-view__contracts-count">{(branch.contracts || []).length}</span>
            </div>

            {(branch.contracts || []).length === 0 ? (
              <div className="branch-view__empty">{t('branches.noContracts')}</div>
            ) : (
              <ul className="branch-view__contract-list">
                {(branch.contracts || []).map((c, i) => {
                  const fi = FILE_ICONS[c.type] ?? FILE_ICONS.pdf;
                  return (
                    <li key={c.id || i} className="branch-view__contract-row">
                      <span className="branch-view__contract-icon" style={{ background: fi.bg }}>{fi.icon}</span>
                      <div className="branch-view__contract-info">
                        <div className="branch-view__contract-name">{c.name}</div>
                        <div className="branch-view__contract-meta">{c.size} · {String(c.type).toUpperCase()}</div>
                      </div>
                      <button type="button" className="btn btn--outline branch-view__open-btn" onClick={() => openContractDocument(c)}>
                        <Icon name="eye" size={14} color="#02c3a6" /> {t('branches.openPreview')}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default BranchDetailPage;
